import { useState, useEffect, useMemo } from "react";

/**
 * useClientPagination — dense client-side pagination for pages that already
 * fetch the full row set (tabs, filters, and search work in one pass).
 *
 * Returns:
 *   pageRows       — current-page slice of `rows`
 *   total          — rows.length (before slicing)
 *   page, setPage
 *   pageSize, setPageSize
 *
 * Auto-resets page to 1 when the source array's length shrinks below the
 * current window (e.g. after search/filter narrows the list).
 */
export default function useClientPagination(rows, storageKey = null, defaultPageSize = 50) {
  const [page, setPage] = useState(1);
  const [pageSize, _setPageSize] = useState(() =>
    storageKey ? Number(localStorage.getItem(`${storageKey}_page_size`)) || defaultPageSize : defaultPageSize
  );

  const total = Array.isArray(rows) ? rows.length : 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Clamp page when data shrinks
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const setPageSize = (n) => {
    _setPageSize(n);
    setPage(1);
    if (storageKey) localStorage.setItem(`${storageKey}_page_size`, String(n));
  };

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (rows || []).slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  return { pageRows, total, page, setPage, pageSize, setPageSize };
}
